import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Star, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;

const Designers = ({ user }) => {
  const navigate = useNavigate();
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [slideIndices, setSlideIndices] = useState({});

  useEffect(() => {
    fetchDesigners();
  }, []);

  const fetchDesigners = async () => {
    try {
      const response = await axios.get(`${API_URL}/designers`);
      setDesigners(response.data);
    } catch (error) {
      console.error('Error fetching designers:', error);
      toast.error('Failed to load designers');
    } finally {
      setLoading(false);
    }
  };

  // Initialize slide indices when designers change
  useEffect(() => {
    const initial = {};
    designers.forEach((d) => { initial[d.id] = 0; });
    setSlideIndices(initial);
  }, [designers]);

  // Auto-advance all cards every 4s where multiple images exist
  useEffect(() => {
    if (!designers || designers.length === 0) return;
    const id = setInterval(() => {
      setSlideIndices((prev) => {
        const next = { ...prev };
        designers.forEach((d) => {
          const imgs = (d.design_images && d.design_images.length > 0) ? d.design_images : (d.portfolio_images || []);
          if (imgs.length > 1) {
            const cur = next[d.id] || 0;
            next[d.id] = (cur + 1) % imgs.length;
          } else {
            next[d.id] = 0;
          }
        });
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [designers]);

  const filteredDesigners = designers.filter(designer => {
    if (filter === 'all') return true;
    const specs = (designer.specialties || []).map((s) => s.toLowerCase());
    return specs.includes(filter.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-24">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text" data-testid="designers-page-title">
          Featured Designers
        </h1>
        <p className="text-gray-400 mb-12 text-lg">
          Browse our curated selection of talented fashion designers ready to serve you.
        </p>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-4 mb-12" data-testid="filter-section">
          {['all', 'traditional', 'western', 'bridal', 'casual'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${filter === f
                ? 'bg-[#d4a574] text-[#0a0a0a]'
                : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a]'
                }`}
              data-testid={`filter-${f}-btn`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Designers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="designers-grid">
          {filteredDesigners.map((designer) => (
            <div
              key={designer.id}
              className="glass-card overflow-hidden card-hover cursor-pointer"
              onClick={() => navigate(`/designers/${designer.id}`)}
              data-testid={`designer-card-${designer.id}`}
            >
              {(() => {
                const designImages = (designer.design_images && designer.design_images.length > 0)
                  ? designer.design_images
                  : (designer.portfolio_images || []);
                const profileImage = (designer.profile_images && designer.profile_images.length > 0)
                  ? designer.profile_images[0]
                  : (designer.portfolio_images && designer.portfolio_images.length > 0 ? designer.portfolio_images[0] : 'https://via.placeholder.com/120x120?text=Designer');
                const idx = slideIndices[designer.id] || 0;
                const designSrc = designImages[idx] || 'https://via.placeholder.com/400x500?text=Design';
                const hasMultiple = designImages.length > 1;

                return (
                  <>
                    <div className="aspect-[3/4] relative overflow-hidden bg-[#111]">
                      <img
                        src={designSrc}
                        alt={`${designer.name} design`}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105 select-none"
                      />
                      {hasMultiple && (
                        <>
                          <button
                            type="button"
                            aria-label="Previous image"
                            onClick={(e) => { e.stopPropagation(); setSlideIndices((prev) => ({ ...prev, [designer.id]: (idx - 1 + designImages.length) % designImages.length })); }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            aria-label="Next image"
                            onClick={(e) => { e.stopPropagation(); setSlideIndices((prev) => ({ ...prev, [designer.id]: (idx + 1) % designImages.length })); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                          <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2">
                            {designImages.map((_, dotIdx) => (
                              <button
                                key={dotIdx}
                                aria-label={`Go to image ${dotIdx + 1}`}
                                onClick={(e) => { e.stopPropagation(); setSlideIndices((prev) => ({ ...prev, [designer.id]: dotIdx })); }}
                                className={`h-2.5 w-2.5 rounded-full ${dotIdx === idx ? 'bg-white' : 'bg-white/40'}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="relative overflow-hidden rounded-3xl border border-[#d4a574]/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(212,165,116,0.08))] p-5 mb-5">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,165,116,0.16),transparent_44%)] pointer-events-none" />
                        <div className="relative flex items-center gap-4">
                          <div className="w-16 h-16 shrink-0 rounded-full p-[2px] bg-gradient-to-br from-[#f0d9b5] via-[#d4a574] to-[#7a5632] shadow-[0_12px_28px_rgba(212,165,116,0.2)]">
                            <div className="w-full h-full rounded-full overflow-hidden bg-[#111]">
                              <img
                                src={profileImage}
                                alt={designer.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase tracking-[0.24em] text-[#d4a574] mb-1">Featured Designer</p>
                            <h3 className="text-2xl font-bold text-white leading-tight truncate mb-2">{designer.name}</h3>
                            <div className="flex flex-wrap items-center gap-2 text-[#d4a574]">
                              <Star className="w-4 h-4 fill-current" />
                              <span className="font-semibold">{designer.rating.toFixed(1)}</span>
                              <span className="text-gray-400 text-sm">({designer.total_reviews} reviews)</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-gray-400 mb-3">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{designer.experience}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {designer.specialties.slice(0, 3).map((specialty, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-xs"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-[#d4a574]">{Number(designer.hourly_rate || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                        <span className="text-gray-400 text-sm">per visit</span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}
        </div>

        {filteredDesigners.length === 0 && (
          <div className="text-center py-20" data-testid="no-designers-message">
            <p className="text-gray-400 text-lg">No designers found for this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Designers;
